import { useState } from 'react';
import { Link } from 'react-router-dom';

const RESIDENTIAL = [
  { title: 'Standard House Cleaning', desc: 'Regular maintenance cleaning covering all living areas, bedrooms, bathrooms, kitchen, and common spaces. Great for weekly or bi-weekly upkeep.', icon: 'ri-home-3-line' },
  { title: 'Deep Cleaning', desc: 'A thorough, top-to-bottom clean that reaches every corner — inside appliances, behind furniture, baseboards, vents, and more. Perfect for first-time customers or seasonal cleans.', icon: 'ri-sparkling-2-line' },
  { title: 'Move In / Move Out Cleaning', desc: 'Make your transition stress-free. We leave the property in pristine condition, helping renters get their full deposit back and new homeowners start fresh.', icon: 'ri-truck-line' },
  { title: 'Post-Construction Cleaning', desc: 'Remove dust, debris, and construction residue after renovations or new builds. We restore your space to a clean, livable condition fast.', icon: 'ri-tools-line' },
  { title: 'Recurring Maintenance Plans', desc: 'Choose weekly, bi-weekly, or monthly cleaning plans designed to keep your home consistently clean year-round at reduced rates.', icon: 'ri-repeat-line' },
];

const COMMERCIAL = [
  { title: 'Office Cleaning', desc: 'Keep your workspace clean, organized, and professional. We service desks, common areas, restrooms, break rooms, and lobbies with minimal disruption.', icon: 'ri-building-2-line' },
  { title: 'Retail Store Cleaning', desc: 'First impressions matter. We ensure your retail space is spotless, sanitized, and welcoming for customers every day.', icon: 'ri-store-2-line' },
  { title: 'Restaurant & Kitchen Cleaning', desc: 'Deep cleaning services for commercial kitchens, dining areas, and food preparation zones to maintain health and safety standards.', icon: 'ri-restaurant-line' },
  { title: 'Medical & Healthcare Facilities', desc: 'Specialized sanitization and disinfection services meeting the stringent cleanliness requirements for clinics, dental offices, and healthcare spaces.', icon: 'ri-first-aid-kit-line' },
  { title: 'Janitorial Services', desc: 'Ongoing daily or nightly janitorial maintenance for offices, warehouses, and commercial properties throughout Port St. Lucie and Martin County.', icon: 'ri-service-line' },
];

type Tab = 'residential' | 'commercial';

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState<Tab>('residential');

  const servicesSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Cleaning Services | A&S Prestige Cleaning Service',
    url: `${SITE_URL}/services`,
    description: 'Comprehensive residential and commercial cleaning services for Port St. Lucie and Martin County, FL including deep cleaning, move in/out, and janitorial services.',
    mainEntity: {
      '@type': 'CleaningService',
      name: 'A&S Prestige Cleaning Service',
      url: SITE_URL,
      telephone: '+15618461326',
      email: 'asprestigecleaningservice62@gmail.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Port St. Lucie',
        addressRegion: 'FL',
        addressCountry: 'US',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Cleaning Services',
        itemListElement: [
          ...RESIDENTIAL.map((s) => ({
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: s.title,
              description: s.desc,
            },
          })),
          ...COMMERCIAL.map((s) => ({
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: s.title,
              description: s.desc,
            },
          })),
        ],
      },
    },
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Book a Cleaning Service with A&S',
    description: 'Book your professional cleaning service in Port St. Lucie and Martin County in 3 easy steps.',
    step: [
      {
        '@type': 'HowToStep',
        name: 'Request a Quote',
        text: 'Fill out our form or call us. We\'ll discuss your needs and provide a free, no-obligation estimate.',
        position: 1,
      },
      {
        '@type': 'HowToStep',
        name: 'Schedule Your Clean',
        text: 'Pick a date and time that works for you. We\'ll confirm your appointment promptly.',
        position: 2,
      },
      {
        '@type': 'HowToStep',
        name: 'Enjoy the Results',
        text: 'Our professional team arrives and transforms your space. Relax and enjoy the clean!',
        position: 3,
      },
    ],
  };

  return (
    <main>
      <title>Cleaning Services Port St. Lucie &amp; Martin County FL | A&S Cleaning Service</title>
      <meta name="description" content="Residential and commercial cleaning services in Port St. Lucie and Martin County, FL. Deep cleaning, move in/out, office cleaning, post-construction &amp; more. Free estimates." />
      <meta name="keywords" content="residential cleaning port st lucie, commercial cleaning martin county, deep cleaning stuart fl, move out cleaning florida, office cleaning jensen beach" />
      <link rel="canonical" href={`${SITE_URL}/services`} />
      <meta property="og:title" content="Cleaning Services | A&S Cleaning Service Port St. Lucie" />
      <meta property="og:description" content="Professional residential and commercial cleaning solutions for Port St. Lucie and Martin County, FL. Book your free estimate today." />
      <meta property="og:url" content={`${SITE_URL}/services`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(servicesSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/40e3bba29ea8dfc1fc5cee31ec111309.jpg"
          alt="A&S Cleaning Service team"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60" />
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <span className="inline-block bg-emerald-600/90 text-white text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-5">
            What We Offer
          </span>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Cleaning Services
          </h1>
          <p className="text-gray-200 text-lg max-w-2xl mx-auto mb-8">
            Comprehensive residential and commercial cleaning solutions for Port St. Lucie and Martin County, FL.
          </p>
          <Link to="/contact" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-10 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block">
            Get a Free Estimate
          </Link>
        </div>
      </section>

      {/* Tab Switcher */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-center mb-12">
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              {(['residential', 'commercial'] as Tab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-8 py-3 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap capitalize ${
                    activeTab === tab ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab === 'residential' ? 'Residential' : 'Commercial'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(activeTab === 'residential' ? RESIDENTIAL : COMMERCIAL).map((s) => (
              <div key={s.title} className="border border-gray-100 rounded-2xl p-7 hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors">
                <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-xl mb-5">
                  <i className={`${s.icon} text-emerald-600 text-2xl`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <span className="text-emerald-600 font-bold text-sm tracking-widest uppercase">How It Works</span>
            <h2 className="text-4xl font-extrabold text-gray-900 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Simple as 1-2-3
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { step: '01', icon: 'ri-file-text-line', title: 'Request a Quote', desc: 'Fill out our form or call us. We\'ll discuss your needs and provide a free, no-obligation estimate.' },
              { step: '02', icon: 'ri-calendar-event-line', title: 'Schedule Your Clean', desc: 'Pick a date and time that works for you. We\'ll confirm your appointment promptly.' },
              { step: '03', icon: 'ri-sparkling-line', title: 'Enjoy the Results', desc: 'Our professional team arrives and transforms your space. Relax and enjoy the clean!' },
            ].map((p) => (
              <div key={p.step} className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 flex items-center justify-center bg-emerald-600 rounded-2xl mx-auto">
                    <i className={`${p.icon} text-white text-3xl`} />
                  </div>
                  <span className="absolute -top-3 -right-3 bg-emerald-100 text-emerald-700 text-xs font-black px-2.5 py-1 rounded-full">{p.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{p.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-emerald-600 py-16">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Not Sure Which Service You Need?
          </h2>
          <p className="text-emerald-100 mb-8">Contact us and we\'ll help you find the perfect cleaning solution for your home or business in Port St. Lucie or Martin County.</p>
          <Link to="/contact" className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-10 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block">
            Talk to Our Team
          </Link>
        </div>
      </section>
    </main>
  );
}
